/**
 * 169 — a service: a product the till sells that is never stock.
 *
 * A photocopy, laminating, fitting a screen protector, cutting a board, a
 * delivery fee. Its quantity is always 0 and the database refuses any stock on
 * it (SERVICE_NO_STOCK, two triggers), so every screen that is about stock —
 * the stocktake, the inventory value, „ამოიწურა", low and dead stock, reorder,
 * transfers, write-off, დაშლა, receiving — leaves it out through these two
 * functions, never through a copy of the rule.
 */
import type { Key } from './i18n'
import { roundWeight } from './weight'

type MaybeService = { is_service?: boolean | null }

export const isService = (p: MaybeService): boolean => p.is_service === true

/** A sale line's product: the shop's products, else the hidden ones (1.68 — the table service product lives in the
 *  store's `serviceRows`, never in `products`). A screen that asks „is this line a service?" reads through this, or the
 *  service line passes as goods (Codex final #2: the waybill). */
export function saleLineProduct<T extends { id: string }>(id: string | null | undefined, products: readonly T[], hidden: readonly T[]): T | undefined {
  if (!id) return undefined
  return products.find((p) => p.id === id) ?? hidden.find((p) => p.id === id)
}

/**
 * What the till may sell of a product: its stock, or no limit for a service —
 * so every „never more than the shelf" check in the till and the kiosk reads one
 * number and a service passes them all. A product that is not there sells 0.
 */
export function sellableStock(p: (MaybeService & { quantity: number }) | undefined | null): number {
  if (!p) return 0
  return isService(p) ? Infinity : p.quantity
}

/**
 * A till return on the screen copy: goods go back on the shelf, a service does
 * not (create_sale moves no stock for it — without this the cache showed the
 * service with stock, and the low-stock bell listed it, until the next read).
 * Every line of a product counts (182: a cup can be a returned line AND a part a
 * returned coffee took).
 */
export function returnedToShelf<P extends MaybeService & { id: string; quantity: number }>(
  products: P[],
  lines: { product_id?: string | null; quantity: number }[],
): P[] {
  return products.map((p) => {
    if (isService(p)) return p
    const back = lineSum(lines, p.id)
    return back ? { ...p, quantity: roundWeight(p.quantity + back) } : p
  })
}

/**
 * A till sale on the screen copy: its lines come off the shelf (never below 0; a service holds
 * no stock). A sale the server answered as a REPLAY was booked by an earlier press — maybe
 * already on screen through a live refresh — so the shelf is left as it is and read again
 * (Codex, 1.57 final review #3: a lost reply, a second press, the water showed 3 for a shelf of 4).
 */
export function soldFromShelf<P extends MaybeService & { id: string; quantity: number }>(
  products: P[],
  lines: { product_id?: string | null; quantity: number }[],
  replayed: boolean,
): P[] {
  if (replayed) return products
  return products.map((p) => {
    if (isService(p)) return p
    const sold = lineSum(lines, p.id)
    return sold ? { ...p, quantity: Math.max(0, roundWeight(p.quantity - sold)) } : p
  })
}

/** All of a product's lines together (182: a cup sold on its own AND taken by a coffee). */
function lineSum(lines: { product_id?: string | null; quantity: number }[], id: string): number {
  let n = 0
  for (const l of lines) if (l.product_id === id) n += l.quantity
  return Math.round(n * 1e6) / 1e6
}

/** The products that ARE stock, in their order. */
export function stockProducts<T extends MaybeService>(ps: readonly T[]): T[] {
  return ps.filter((p) => !isService(p))
}

/**
 * Switching goods to a service while some branch still holds them would hide
 * real stock; the server refuses it, and the form says so first. Switching
 * back is always fine (it starts at 0).
 */
export function serviceSwitchError(isServiceNow: boolean, wantService: boolean, stockAnywhere: number): Key | null {
  if (wantService && !isServiceNow && stockAnywhere !== 0) return 'wh.svc.errStock'
  return null
}
